import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import { combineReducers } from '@reduxjs/toolkit'

// Custom storage using localStorage (for redux-persist)
const createNoopStorage = () => ({
  getItem: (_key: string) => Promise.resolve(null),
  setItem: (_key: string, _value: string) => Promise.resolve(),
  removeItem: (_key: string) => Promise.resolve(),
})

const storage = typeof window !== 'undefined'
  ? {
      getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
      setItem: (key: string, value: string) => { localStorage.setItem(key, value); return Promise.resolve() },
      removeItem: (key: string) => { localStorage.removeItem(key); return Promise.resolve() },
    }
  : createNoopStorage()

// ─── Auth Slice ───────────────────────────────────────────
interface AuthState {
  sessionToken: string | null
  userId: string | null
  walletName: string | null
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    sessionToken: null,
    userId: null,
    walletName: null,
  } as AuthState,
  reducers: {
    setSession(state, action: PayloadAction<{ sessionToken: string; userId: string }>) {
      state.sessionToken = action.payload.sessionToken
      state.userId = action.payload.userId
    },
    clearSession(state) {
      state.sessionToken = null
      state.userId = null
    },
    setWalletName(state, action: PayloadAction<string>) {
      state.walletName = action.payload
    },
  },
})

// ─── Platform Slice ───────────────────────────────────────
interface PlatformState {
  platformCode: string | null
  emailId: string | null
  usdAmount: string | null
}

const platformSlice = createSlice({
  name: 'platform',
  initialState: {
    platformCode: null,
    emailId: null,
    usdAmount: null,
  } as PlatformState,
  reducers: {
    setPlatformCode(state, action: PayloadAction<string>) {
      state.platformCode = action.payload
    },
    setEmailId(state, action: PayloadAction<string>) {
      state.emailId = action.payload
    },
    setUsdAmount(state, action: PayloadAction<string | null>) {
      state.usdAmount = action.payload
    },
    clearPlatform(state) {
      state.platformCode = null
      state.emailId = null
      state.usdAmount = null
    },
  },
})

// ─── Pending Transaction Slice ────────────────────────────
interface PendingTransactionState {
  transactionId: string | null
  delay: string | null
}

const pendingTransactionSlice = createSlice({
  name: 'pendingTransaction',
  initialState: {
    transactionId: null,
    delay: null,
  } as PendingTransactionState,
  reducers: {
    setPendingTransaction(state, action: PayloadAction<{ transactionId: string; delay?: string }>) {
      state.transactionId = action.payload.transactionId
      state.delay = action.payload.delay || null
    },
    clearPendingTransaction(state) {
      state.transactionId = null
      state.delay = null
    },
  },
})

// ─── Admin Slice ──────────────────────────────────────────
interface AdminState {
  token: string | null
  data: string | null // JSON string of admin data
}

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    token: null,
    data: null,
  } as AdminState,
  reducers: {
    setAdminAuth(state, action: PayloadAction<{ token: string; data: string }>) {
      state.token = action.payload.token
      state.data = action.payload.data
    },
    clearAdminAuth(state) {
      state.token = null
      state.data = null
    },
  },
})

// ─── Combined reducer with persist ───────────────────────
const rootReducer = combineReducers({
  auth: authSlice.reducer,
  platform: platformSlice.reducer,
  pendingTransaction: pendingTransactionSlice.reducer,
  admin: adminSlice.reducer,
})

const persistConfig = {
  key: 'irma',
  storage,
  whitelist: ['auth', 'platform', 'pendingTransaction', 'admin'],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

// ─── Store ────────────────────────────────────────────────
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
})

export const persistor = persistStore(store)

// ─── Types ────────────────────────────────────────────────
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// ─── Export actions ───────────────────────────────────────
export const { setSession, clearSession, setWalletName } = authSlice.actions
export const { setPlatformCode, setEmailId, setUsdAmount, clearPlatform } = platformSlice.actions
export const { setPendingTransaction, clearPendingTransaction } = pendingTransactionSlice.actions
export const { setAdminAuth, clearAdminAuth } = adminSlice.actions
